import download from 'downloadjs'
import { combineEpics } from 'redux-observable'
import { Observable } from 'rxjs/Observable'

import { openInNewTab } from 'utils/DOM'

import { addToast } from 'containers/Toasts/actions'

import { mobileSwitcher } from '../actions'
import { MAP_INCIDENT_TYPE_TO_URL_PREFIX } from '../constants'
import { openChangedStateModal } from '../ReportCommunicationModal/actions'

import {
  changeAssigneesError,
  changeAssigneesSuccess,
  changeReportStateError,
  changeReportStateSuccess,
  closeIncidentError,
  closeIncidentSuccess,
  closeSafeChallengeError,
  closeSafeChallengeSuccess,
  downloadOriginalEmailError,
  downloadOriginalEmailSuccess,
  fetchAffMailboxesCountsError,
  fetchAffMailboxesCountsSuccess,
  fetchAssigneesError,
  fetchAssigneesSuccess,
  fetchCommunityInsightsError,
  fetchCommunityInsightsSuccess,
  fetchElementsError,
  fetchElementsSuccess,
  fetchEmailSnapshotError,
  fetchEmailSnapshotSuccess,
  fetchReportError,
  fetchReportSuccess,
  fetchScansFreemiumLimitError,
  fetchScansFreemiumLimitSuccess,
  keepEncryptedIncidentInQuarantineError,
  keepEncryptedIncidentInQuarantineSuccess,
  releaseEncryptedIncidentError,
  releaseEncryptedIncidentSuccess,
  updateElementsError,
  updateElementsSuccess,
  validateNewAffectedMailboxesSeenError,
  validateNewAffectedMailboxesSeenSuccess,
} from './actions'
import {
  CHANGE_ASSIGNEES_ASYNC,
  CLOSE_INCIDENT_ASYNC,
  CLOSE_SAFE_CHALLENGE_INCIDENT_ASYNC,
  DOWNLOAD_ORIGINAL_EMAIL_ASYNC,
  EMAIL_SNAPSHOT_ASYNC,
  FETCH_AFFECTED_MAILBOXES_COUNTS_ASYNC,
  FETCH_ASSIGNEES_ASYNC,
  FETCH_COMMUNITY_INSIGHTS_ASYNC,
  FETCH_SCANS_FREEMIUM_LIMIT_ASYNC,
  KEEP_ENCRYPTED_INCIDENT_IN_QUARANTINE_ASYNC,
  OPEN_EMAIL_SNAPSHOT_NEW_TAB,
  RELEASE_ENCRYPTED_INCIDENT_ASYNC,
  REPORT_ELEMENTS_REQUEST_ASYNC,
  REPORT_ELEMENTS_UPDATE_ASYNC,
  REPORT_REQUEST_ASYNC,
  REPORT_STATE_CHANGE_ASYNC,
  VALIDATE_NEW_AFFECTED_MAILBOXES_SEEN_ASYNC,
} from './constants'

const fetchReportEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(REPORT_REQUEST_ASYNC.INITIATE).switchMap((action) =>
    api.resolutionCenter
      .getReport(
        action.payload.reportId,
        MAP_INCIDENT_TYPE_TO_URL_PREFIX[action.payload.reportType]
      )
      .map((response) => fetchReportSuccess(response))
      .catch((error) => Observable.of(epicsErrorHandler(fetchReportError, error, [404])))
      .takeUntil(action$.ofType(REPORT_REQUEST_ASYNC.CANCEL))
  )

const fetchCommunityInsightsEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(FETCH_COMMUNITY_INSIGHTS_ASYNC.INITIATE).mergeMap((action) =>
    api.resolutionCenter
      .getCommunityInsights(action.payload.reportId)
      .map((response) => fetchCommunityInsightsSuccess(response))
      .catch((error) => Observable.of(epicsErrorHandler(fetchCommunityInsightsError, error, [404])))
      .takeUntil(action$.ofType(FETCH_COMMUNITY_INSIGHTS_ASYNC.CANCEL))
  )

const fetchAssigneesEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(FETCH_ASSIGNEES_ASYNC.INITIATE).mergeMap((action) =>
    api.resolutionCenter
      .getAssignees(store.getState().partnerOptions.selectedCompany?.value, action.payload.search)
      .map((response) => fetchAssigneesSuccess(response))
      .catch((error) => Observable.of(epicsErrorHandler(fetchAssigneesError, error)))
      .takeUntil(action$.ofType(FETCH_ASSIGNEES_ASYNC.CANCEL))
  )

const changeAssigneeEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(CHANGE_ASSIGNEES_ASYNC.INITIATE).mergeMap((action) =>
    api.resolutionCenter
      .changeAssignee(
        action.payload.reportId,
        { assignee_id: action.payload.assigneeId },
        MAP_INCIDENT_TYPE_TO_URL_PREFIX[action.payload.reportType]
      )
      .map((response) => changeAssigneesSuccess(response))
      .catch((error) => Observable.of(epicsErrorHandler(changeAssigneesError, error)))
      .takeUntil(action$.ofType(CHANGE_ASSIGNEES_ASYNC.CANCEL))
  )

const changeReportStateEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(REPORT_STATE_CHANGE_ASYNC.INITIATE).mergeMap((action) =>
    api.resolutionCenter
      .changeReportState(
        action.payload.reportId,
        action.payload.newState,
        MAP_INCIDENT_TYPE_TO_URL_PREFIX[action.payload.reportType]
      )
      .mergeMap((response) => {
        mobileSwitcher.showList()
        return Observable.of(
          changeReportStateSuccess(response, action.payload.newState, action.payload.reportType),
          action.payload.newState !== 'delete' &&
            openChangedStateModal({
              reportId: action.payload.reportId,
              reportType: action.payload.reportType,
              newState: action.payload.newState,
              isFederation: response.response?.reportIsFederation,
              allowTags: response.response?.allowTags,
              communityUserExists: response.response?.communityUserExists,
              mitigationsCount: response.response?.mitigationsCount,
              classificationTexts: response.response?.classificationTexts,
            })
        )
      })
      .catch((error) => Observable.of(epicsErrorHandler(changeReportStateError, error)))
      .takeUntil(action$.ofType(REPORT_STATE_CHANGE_ASYNC.CANCEL))
  )

const updateElementsEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(REPORT_ELEMENTS_UPDATE_ASYNC.INITIATE).mergeMap((action) =>
    api.resolutionCenter
      .updateElements(
        action.payload.reportId,
        action.payload.elementsName,
        MAP_INCIDENT_TYPE_TO_URL_PREFIX[action.payload.reportType]
      )
      .map((response) => updateElementsSuccess(response))
      .catch((error) => Observable.of(epicsErrorHandler(updateElementsError, error)))
      .takeUntil(action$.ofType(REPORT_ELEMENTS_UPDATE_ASYNC.CANCEL))
  )

const fetchElementsEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(REPORT_ELEMENTS_REQUEST_ASYNC.INITIATE).mergeMap((action) =>
    api.resolutionCenter
      .fetchElements(
        action.payload.reportId,
        action.payload.elementsName,
        MAP_INCIDENT_TYPE_TO_URL_PREFIX[action.payload.reportType]
      )
      .map((response) =>
        fetchElementsSuccess(response, action.payload.reportId, action.payload.elementsName)
      )
      .catch((error) => Observable.of(epicsErrorHandler(fetchElementsError, error)))
      .takeUntil(action$.ofType(REPORT_ELEMENTS_REQUEST_ASYNC.CANCEL))
  )

const validateNewAffectedMailboxesSeen = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(VALIDATE_NEW_AFFECTED_MAILBOXES_SEEN_ASYNC.INITIATE).mergeMap(() =>
    api.resolutionCenter
      .validateNewAffectedMailboxesSeen()
      .map((response) => validateNewAffectedMailboxesSeenSuccess(response))
      .catch((error) =>
        Observable.of(epicsErrorHandler(validateNewAffectedMailboxesSeenError, error))
      )
      .takeUntil(action$.ofType(VALIDATE_NEW_AFFECTED_MAILBOXES_SEEN_ASYNC.CANCEL))
  )

const fetchAffMailboxesCountsEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(FETCH_AFFECTED_MAILBOXES_COUNTS_ASYNC.INITIATE).mergeMap((action) =>
    api.resolutionCenter
      .fetchAffMailboxesCounts(action.payload.reportId)
      .map((response) => fetchAffMailboxesCountsSuccess(response))
      .catch((error) => Observable.of(epicsErrorHandler(fetchAffMailboxesCountsError, error)))
  )

const fetchScansFreemiumLimitEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(FETCH_SCANS_FREEMIUM_LIMIT_ASYNC.INITIATE).mergeMap(() =>
    api.resolutionCenter
      .fetchScansFreemiumLimit()
      .map((response) => fetchScansFreemiumLimitSuccess(response))
      .catch((error) => Observable.of(epicsErrorHandler(fetchScansFreemiumLimitError, error)))
  )

const downloadOriginalEmailEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(DOWNLOAD_ORIGINAL_EMAIL_ASYNC.INITIATE).mergeMap((action) =>
    api.resolutionCenter
      .fetchRawEmail(action.payload.reportId, action.payload.emailType)
      .map((response) => {
        download(response, 'body-raw.html')
        return downloadOriginalEmailSuccess(response)
      })
      .catch((error) => Observable.of(epicsErrorHandler(downloadOriginalEmailError, error)))
  )

const closeIncidentEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(CLOSE_INCIDENT_ASYNC.INITIATE).mergeMap((action) =>
    api.resolutionCenter
      .closeIncident(action.payload.reportId)
      .map((response) => closeIncidentSuccess(response))
      .catch((error) => Observable.of(epicsErrorHandler(closeIncidentError, error, [403])))
  )

const closeSafeChallengeIncidentEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(CLOSE_SAFE_CHALLENGE_INCIDENT_ASYNC.INITIATE).mergeMap((action) =>
    api.resolutionCenter
      .closeSafeChallengeIncident(action.payload.reportId)
      .map((response) => closeSafeChallengeSuccess(response))
      .catch((error) => Observable.of(epicsErrorHandler(closeSafeChallengeError, error)))
  )

const emailSnapshotEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(EMAIL_SNAPSHOT_ASYNC.INITIATE, OPEN_EMAIL_SNAPSHOT_NEW_TAB).mergeMap((action) =>
    api.resolutionCenter
      .getEmailSnapshot(action.payload.reportId)
      .map((response) => {
        if (action.type === OPEN_EMAIL_SNAPSHOT_NEW_TAB) {
          openInNewTab(response.emailBodyPreviewUrl)
        }
        return fetchEmailSnapshotSuccess(response)
      })
      .catch((error) =>
        Observable.of(epicsErrorHandler(fetchEmailSnapshotError, error, [403, 404]))
      )
  )

const releaseEncryptedIncidentEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(RELEASE_ENCRYPTED_INCIDENT_ASYNC.INITIATE).mergeMap((action) =>
    api.resolutionCenter
      .releaseEncryptedIncident(action.payload.reportId)
      .mergeMap((response) =>
        Observable.of(
          releaseEncryptedIncidentSuccess(response),
          addToast({ message: 'The email has been released to the end user inbox.' })
        )
      )
      .catch((error) =>
        Observable.of(
          epicsErrorHandler(releaseEncryptedIncidentError, error),
          addToast({ message: error.response?.message || error.response?.detail, type: 'error' })
        )
      )
  )

const keepEncryptedIncidentInQuarantine = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(KEEP_ENCRYPTED_INCIDENT_IN_QUARANTINE_ASYNC.INITIATE).mergeMap((action) =>
    api.resolutionCenter
      .keepEncryptedIncidentInQuarantine(action.payload.reportId)
      .map((response) => keepEncryptedIncidentInQuarantineSuccess(response))
      .catch((error) =>
        Observable.of(epicsErrorHandler(keepEncryptedIncidentInQuarantineError, error, [403, 404]))
      )
  )

export default combineEpics(
  fetchReportEpic,
  fetchCommunityInsightsEpic,
  fetchAssigneesEpic,
  changeReportStateEpic,
  updateElementsEpic,
  fetchElementsEpic,
  validateNewAffectedMailboxesSeen,
  fetchAffMailboxesCountsEpic,
  fetchScansFreemiumLimitEpic,
  changeAssigneeEpic,
  downloadOriginalEmailEpic,
  closeIncidentEpic,
  closeSafeChallengeIncidentEpic,
  releaseEncryptedIncidentEpic,
  keepEncryptedIncidentInQuarantine,
  emailSnapshotEpic
)
